// Two-way URL ↔ Redux filter sync for any route that hosts a FilterPanel
// + sort controls. Read on mount, write on change.
//
//   useFilterUrlSync({
//       context: 'commanders',         // matches getRangeAxesForContext
//       filters, sortBy, sortDirection, // current state (from Redux)
//       onApply: ({ filters, sortBy, sortDirection }) => { ... },
//   })
//
// `onApply` fires exactly once, on mount, with whatever the URL
// resolves to (defaults filled in for missing keys). The route should
// dispatch its filtered-fetch action there so the initial list reflects
// the link the user opened. Subsequent state changes write back to the
// URL via replaceState (debounced — typing in the search box shouldn't
// pollute history).

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { filtersToSearchParams, searchParamsToFilters } from './filterUrl';

const WRITE_DEBOUNCE_MS = 250;

export function useFilterUrlSync({ context, filters, sortBy, sortDirection, onApply }) {
    const location = useLocation();
    const navigate = useNavigate();

    const appliedRef = useRef(false);
    const onApplyRef = useRef(onApply);
    onApplyRef.current = onApply;

    useEffect(() => {
        if (appliedRef.current) return;
        appliedRef.current = true;
        const parsed = searchParamsToFilters(new URLSearchParams(location.search), context);
        onApplyRef.current?.(parsed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const writeTimer = useRef(null);
    useEffect(() => {
        if (!appliedRef.current) return undefined;
        if (writeTimer.current) clearTimeout(writeTimer.current);
        writeTimer.current = setTimeout(() => {
            const params = filtersToSearchParams(filters, sortBy, sortDirection, context);
            const qs = params.toString();
            const nextSearch = qs ? `?${qs}` : '';
            if (nextSearch === location.search) return;
            navigate(`${location.pathname}${nextSearch}${location.hash}`, { replace: true });
        }, WRITE_DEBOUNCE_MS);
        return () => writeTimer.current && clearTimeout(writeTimer.current);
    }, [filters, sortBy, sortDirection, context, location.pathname, location.hash, location.search, navigate]);
}
